import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  Waves, SignOut, Users, Globe, CreditCard, Plus, Minus, Trash, EyeSlash,
  ArrowClockwise, Spinner, ChartBar, Eye, Copy, CheckCircle,
  CaretDown, CaretUp, Receipt, Envelope, Wallet, MagnifyingGlass,
  ShoppingCart, X, Lightning, Plugs, PencilSimple, Power,
  Sun, Moon, CurrencyInr, Bank, Bell, PaperPlaneTilt, Check,
  XCircle, Gear, DownloadSimple, ClockCounterClockwise, FileCsv
} from '@phosphor-icons/react';

import { API_URL } from '../utils/api';
import { Logo, LogoText } from '../components/Logo';

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [indexerBalance, setIndexerBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditAction, setCreditAction] = useState('add');
  const [sendEmail, setSendEmail] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [userDetailData, setUserDetailData] = useState(null);
  const [userCreditHistory, setUserCreditHistory] = useState([]);
  const [userPurchaseHistory, setUserPurchaseHistory] = useState([]);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [indexingApis, setIndexingApis] = useState([]);
  const [showAddApiModal, setShowAddApiModal] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [apiForm, setApiForm] = useState({ name: '', base_url: '', api_key: '' });
  const [savingApi, setSavingApi] = useState(false);

  // INR Payments
  const [inrPayments, setInrPayments] = useState([]);
  // UPI Settings
  const [upiSettings, setUpiSettings] = useState({ upi_id: '', display_name: '' });
  const [savingUpi, setSavingUpi] = useState(false);
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  // Send Credits
  const [showSendCreditsModal, setShowSendCreditsModal] = useState(false);
  const [sendCreditsForm, setSendCreditsForm] = useState({ user_email: '', amount: '', note: '', send_email: false });
  const [sendingCredits, setSendingCredits] = useState(false);
  // Activity Log
  const [activityLog, setActivityLog] = useState([]);
  // Receipt Modal
  const [receiptData, setReceiptData] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  // Coupons
  const [coupons, setCoupons] = useState([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', is_active: true });
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [savingCoupon, setSavingCoupon] = useState(false);
  // Custom Packages
  const [customPackages, setCustomPackages] = useState([]);
  const [showCustomPkgModal, setShowCustomPkgModal] = useState(false);
  const [customPkgForm, setCustomPkgForm] = useState({ user_email: '', credits: '', price_usd: '', name: 'Custom' });
  const [savingCustomPkg, setSavingCustomPkg] = useState(false);
  // Payment Settings
  const [paymentSettings, setPaymentSettings] = useState({ inr_enabled: false, crypto_enabled: true });
  const [savingPaySettings, setSavingPaySettings] = useState(false);
  // User URL History
  const [userUrls, setUserUrls] = useState([]);
  const [loadingUserUrls, setLoadingUserUrls] = useState(false);
  // Master API Key
  const [masterApiKey, setMasterApiKey] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    if (searchEmail.trim() === '') { setFilteredUsers(users); }
    else {
      setFilteredUsers(users.filter(u => u.email.toLowerCase().includes(searchEmail.toLowerCase()) || u.name.toLowerCase().includes(searchEmail.toLowerCase())));
    }
  }, [searchEmail, users]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, submissionsRes, statsRes, historyRes, balanceRes, purchasesRes, apisRes, inrRes, upiRes, notifRes, actRes, couponsRes, customPkgRes, paySettingsRes, masterKeyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/submissions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/credit-history`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/indexer-balance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/purchase-history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/indexing-apis`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/inr-payments`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/upi-settings`, { withCredentials: true }).catch(() => ({ data: { upi_id: '', display_name: '' } })),
        axios.get(`${API_URL}/api/admin/notifications`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/activity-log`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/coupons`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/custom-packages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/payment-settings`, { withCredentials: true }).catch(() => ({ data: { inr_enabled: false, crypto_enabled: true } })),
        axios.get(`${API_URL}/api/admin/master-api-key`, { withCredentials: true }).catch(() => ({ data: { key: null } }))
      ]);
      setUsers(usersRes.data);
      setFilteredUsers(usersRes.data);
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
      setCreditHistory(historyRes.data);
      setIndexerBalance(balanceRes.data);
      setPurchaseHistory(purchasesRes.data || []);
      setIndexingApis(apisRes.data || []);
      setInrPayments(inrRes.data || []);
      setUpiSettings(upiRes.data || { upi_id: '', display_name: '' });
      setNotifications(notifRes.data || []);
      setActivityLog(actRes.data || []);
      setCoupons(couponsRes.data || []);
      setCustomPackages(customPkgRes.data || []);
      setPaymentSettings(paySettingsRes.data || { inr_enabled: false, crypto_enabled: true });
      setMasterApiKey(masterKeyRes.data || null);
    } catch (err) { setError('Failed to fetch admin data'); }
    setLoading(false);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const openCreditModal = (userItem, action) => {
    setSelectedUser(userItem); setCreditAction(action); setCreditAmount(''); setSendEmail(false); setShowCreditModal(true);
  };

  const openUserDetailModal = async (userItem) => {
    setUserDetailData(userItem); setShowUserDetailModal(true); setLoadingUserDetail(true);
    setUserUrls([]);
    try {
      const [creditRes, purchaseRes, urlsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/user/${userItem._id}/credit-history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/user/${userItem._id}/purchase-history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/user/${userItem._id}/urls`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setUserCreditHistory(creditRes.data || []);
      setUserPurchaseHistory(purchaseRes.data || []);
      setUserUrls(urlsRes.data || []);
    } catch (err) { console.error('Error fetching user details:', err); }
    setLoadingUserDetail(false);
  };

  const handleCreditUpdate = async () => {
    if (!creditAmount || parseInt(creditAmount) <= 0) { setError('Please enter a valid amount'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/credits`, {
        user_id: selectedUser._id, amount: parseInt(creditAmount), action: creditAction, send_email: sendEmail
      }, { withCredentials: true });
      setSuccess(`Successfully ${creditAction === 'add' ? 'added' : 'subtracted'} ${creditAmount} credits${sendEmail ? ' (email sent)' : ''}`);
      setShowCreditModal(false);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to update credits'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/user/${userId}`, { withCredentials: true });
      setSuccess('User deleted successfully'); fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to delete user'); }
  };

  const copyAllUrls = (urlList, id) => { navigator.clipboard.writeText(urlList.join('\n')); setCopiedId(`all-${id}`); setTimeout(() => setCopiedId(null), 2000); };
  const copyUrl = (url, id) => { navigator.clipboard.writeText(url); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };

  const fetchApis = async () => {
    try { const res = await axios.get(`${API_URL}/api/admin/indexing-apis`, { withCredentials: true }); setIndexingApis(res.data || []); }
    catch (err) { console.error('Error fetching APIs:', err); }
  };

  const handleSaveApi = async () => {
    setSavingApi(true);
    try {
      if (editingApi) { await axios.put(`${API_URL}/api/admin/indexing-apis/${editingApi._id}`, apiForm, { withCredentials: true }); setSuccess('API updated'); }
      else { await axios.post(`${API_URL}/api/admin/indexing-apis`, apiForm, { withCredentials: true }); setSuccess('API added'); }
      setShowAddApiModal(false); setEditingApi(null); setApiForm({ name: '', base_url: '', api_key: '' }); fetchApis();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save API'); }
    setSavingApi(false);
  };

  const handleActivateApi = async (apiId) => {
    try { const res = await axios.post(`${API_URL}/api/admin/indexing-apis/${apiId}/activate`, {}, { withCredentials: true }); setSuccess(res.data.message); fetchApis(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to activate API'); }
  };

  const handleDeleteApi = async (apiId) => {
    if (!window.confirm('Delete this API?')) return;
    try { await axios.delete(`${API_URL}/api/admin/indexing-apis/${apiId}`, { withCredentials: true }); setSuccess('API deleted'); fetchApis(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to delete API'); }
  };

  const openEditApi = (api) => { setEditingApi(api); setApiForm({ name: api.name, base_url: api.base_url, api_key: api.api_key }); setShowAddApiModal(true); };
  const openAddApi = () => { setEditingApi(null); setApiForm({ name: '', base_url: '', api_key: '' }); setShowAddApiModal(true); };

  // INR Payment handlers
  const handleApproveInr = async (paymentId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/inr-payments/${paymentId}/approve`, {}, { withCredentials: true });
      setSuccess(res.data.message);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleRejectInr = async (paymentId) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/inr-payments/${paymentId}/reject`, { reason }, { withCredentials: true });
      setSuccess(res.data.message);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to reject'); }
  };

  // UPI Settings handler
  const handleSaveUpi = async () => {
    if (!upiSettings.upi_id.trim()) { setError('UPI ID is required'); return; }
    setSavingUpi(true);
    try {
      await axios.put(`${API_URL}/api/admin/upi-settings`, upiSettings, { withCredentials: true });
      setSuccess('UPI settings updated');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save UPI settings'); }
    setSavingUpi(false);
  };

  // Notification handler
  const markNotifRead = async (notifId) => {
    try {
      await axios.post(`${API_URL}/api/admin/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n._id === notifId ? { ...n, is_read: true } : n));
    } catch (err) { console.error(err); }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Send Credits handler
  const handleSendCredits = async () => {
    if (!sendCreditsForm.user_email || !sendCreditsForm.amount || parseInt(sendCreditsForm.amount) <= 0) {
      setError('Email and valid amount required');
      return;
    }
    setSendingCredits(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/send-credits`, {
        user_email: sendCreditsForm.user_email,
        amount: parseInt(sendCreditsForm.amount),
        note: sendCreditsForm.note,
        send_email: sendCreditsForm.send_email
      }, { withCredentials: true });
      setSuccess(res.data.message);
      setShowSendCreditsModal(false);
      setSendCreditsForm({ user_email: '', amount: '', note: '', send_email: false });
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to send credits'); }
    setSendingCredits(false);
  };

  const getInrStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 font-medium">Pending</span>;
      case 'approved': return <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-400 font-medium">Approved</span>;
      case 'rejected': return <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400 font-medium">Rejected</span>;
      default: return <span className="px-2 py-0.5 text-xs rounded-full" style={{ background: 'var(--accent-bg)', color: 'var(--text-muted)' }}>{status}</span>;
    }
  };

  // CSV Export
  const handleExport = async (type) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/export/${type}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${type}_export.csv`; a.click();
      window.URL.revokeObjectURL(url);
      setSuccess(`${type} exported`);
    } catch (err) { setError('Export failed'); }
  };

  // Receipt viewer
  const viewReceipt = async (type, id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/receipt/${type}/${id}`, { withCredentials: true });
      setReceiptData(res.data);
      setShowReceiptModal(true);
    } catch (err) { setError('Failed to load receipt'); }
  };

  const getActivityIcon = (action) => {
    if (action.includes('credit')) return <CreditCard weight="duotone" className="w-4 h-4" />;
    if (action.includes('inr')) return <CurrencyInr weight="duotone" className="w-4 h-4" />;
    if (action.includes('user')) return <Users weight="duotone" className="w-4 h-4" />;
    if (action.includes('csv')) return <FileCsv weight="duotone" className="w-4 h-4" />;
    return <Lightning weight="duotone" className="w-4 h-4" />;
  };

  useEffect(() => {
    if (success || error) { const timer = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(timer); }
  }, [success, error]);

  const getUserEmail = (userId) => { const foundUser = users.find(u => u._id === userId); return foundUser?.email || userId; };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'finished': return <span className="px-2 py-1 text-xs rounded bg-emerald-500/20 text-emerald-400">Completed</span>;
      case 'waiting': return <span className="px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-400">Waiting</span>;
      case 'confirming': return <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400">Confirming</span>;
      case 'expired': return <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400">Expired</span>;
      case 'failed': return <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400">Failed</span>;
      default: return <span className="px-2 py-1 text-xs rounded" style={{ background: 'var(--accent-bg)', color: 'var(--text-muted)' }}>{status}</span>;
    }
  };

  const tabStyle = (isActive) => isActive
    ? { background: 'var(--accent)', color: '#ffffff' }
    : { color: 'var(--text-secondary)' };


  // ===== COUPON HANDLERS =====
  const openCouponModal = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setCouponForm({ code: coupon.code, discount_type: coupon.discount_type, discount_value: coupon.discount_value, max_uses: coupon.max_uses || '', is_active: coupon.is_active });
    } else {
      setEditingCoupon(null);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', is_active: true });
    }
    setShowCouponModal(true);
  };

  const saveCoupon = async () => {
    setSavingCoupon(true);
    try {
      if (editingCoupon) {
        await axios.put(`${API_URL}/api/admin/coupons/${editingCoupon._id}`, { ...couponForm, discount_value: parseFloat(couponForm.discount_value), max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null }, { withCredentials: true });
        setSuccess('Coupon updated');
      } else {
        await axios.post(`${API_URL}/api/admin/coupons`, { ...couponForm, discount_value: parseFloat(couponForm.discount_value), max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null }, { withCredentials: true });
        setSuccess('Coupon created');
      }
      setShowCouponModal(false);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save coupon'); }
    setSavingCoupon(false);
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/coupons/${id}`, { withCredentials: true });
      setSuccess('Coupon deleted');
      fetchData();
    } catch (err) { setError('Failed to delete coupon'); }
  };

  // ===== CUSTOM PACKAGE HANDLERS =====
  const saveCustomPackage = async () => {
    setSavingCustomPkg(true);
    try {
      await axios.post(`${API_URL}/api/admin/custom-packages`, {
        user_email: customPkgForm.user_email,
        credits: parseInt(customPkgForm.credits),
        price_usd: parseFloat(customPkgForm.price_usd),
        name: customPkgForm.name
      }, { withCredentials: true });
      setSuccess('Custom package created');
      setShowCustomPkgModal(false);
      setCustomPkgForm({ user_email: '', credits: '', price_usd: '', name: 'Custom' });
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create package'); }
    setSavingCustomPkg(false);
  };

  const deleteCustomPackage = async (id) => {
    if (!window.confirm('Delete this custom package?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/custom-packages/${id}`, { withCredentials: true });
      setSuccess('Custom package deleted');
      fetchData();
    } catch (err) { setError('Failed to delete package'); }
  };

  // ===== PAYMENT SETTINGS HANDLER =====
  const savePaymentSettings = async () => {
    setSavingPaySettings(true);
    try {
      await axios.put(`${API_URL}/api/admin/payment-settings`, paymentSettings, { withCredentials: true });
      setSuccess('Payment settings updated');
    } catch (err) { setError('Failed to update payment settings'); }
    setSavingPaySettings(false);
  };

  // ===== MASTER API KEY HANDLERS =====
  const generateMasterKey = async () => {
    if (masterApiKey?.key && !window.confirm('This will revoke the current key. All apps using it will stop working. Continue?')) return;
    setGeneratingKey(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/master-api-key/generate`, {}, { withCredentials: true });
      setMasterApiKey(res.data);
      setShowApiKey(true);
      setSuccess('New API key generated');
    } catch (err) { setError('Failed to generate key'); }
    setGeneratingKey(false);
  };

  const revokeMasterKey = async () => {
    if (!window.confirm('Revoke the API key? All apps using it will stop working.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/master-api-key`, { withCredentials: true });
      setMasterApiKey(null);
      setShowApiKey(false);
      setSuccess('API key revoked');
    } catch (err) { setError('Failed to revoke key'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
  };


  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="admin-logo">
            <Logo size="sm" />
            <span className="font-black text-base sm:text-xl tracking-tight whitespace-nowrap">ADMIN <span style={{ color: '#f07d1a' }}>PANEL</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-sm hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>{user?.email}</span>
            {/* Notification Bell */}
            <div className="relative">
              <button onClick={() => setShowNotifDropdown(!showNotifDropdown)} className="p-2 rounded-lg transition-colors relative" style={{ color: 'var(--text-secondary)' }} data-testid="notif-bell">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unreadCount}</span>
                )}
              </button>
              {showNotifDropdown && (
                <div className="absolute right-0 top-12 w-80 max-h-80 overflow-y-auto border rounded-xl shadow-2xl z-50" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
                  <div className="p-3 border-b font-bold text-sm" style={{ borderColor: 'var(--border-main)' }}>Notifications</div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No notifications</div>
                  ) : (
                    notifications.slice(0, 15).map(n => (
                      <div key={n._id} className="p-3 border-b text-xs cursor-pointer transition-colors" style={{ borderColor: 'var(--border-main)', background: n.is_read ? 'transparent' : 'rgba(124,58,237,0.05)' }}
                        onClick={() => { markNotifRead(n._id); if (n.type === 'inr_payment') setActiveTab('inr_payments'); setShowNotifDropdown(false); }}>
                        <p className={`${n.is_read ? '' : 'font-bold'}`} style={{ color: 'var(--text-primary)' }}>{n.message}</p>
                        <p className="mt-1" style={{ color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {/* Send Credits Button */}
            <button onClick={() => setShowSendCreditsModal(true)} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} data-testid="send-credits-btn">
              <PaperPlaneTilt weight="fill" className="w-3.5 h-3.5" /> Send Credits
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="admin-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="admin-logout">
              <SignOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-20 sm:pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm mb-6 rounded-lg" data-testid="admin-error">{error}</div>}
          {success && <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 text-sm mb-6 rounded-lg" data-testid="admin-success">{success}</div>}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[
              { icon: Users, value: stats?.total_users || 0, label: 'Total Users', iconBg: 'var(--accent)', iconColor: '#fff' },
              { icon: ChartBar, value: stats?.total_submissions || 0, label: 'Total Batches', iconBg: 'rgba(16,185,129,0.15)', iconColor: '#34D399' },
              { icon: Globe, value: stats?.total_urls_indexed || 0, label: 'URLs Indexed', iconBg: 'rgba(245,158,11,0.15)', iconColor: '#FBBF24' },
              { icon: Wallet, value: indexerBalance?.success ? (indexerBalance?.credits?.available || 0).toLocaleString() : 'N/A', label: 'API Balance', iconBg: 'var(--accent-bg)', iconColor: 'var(--accent-light)' },
            ].map((s, i) => (
              <div key={i} className="card-dashboard flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.iconBg }}>
                  <s.icon weight="duotone" className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: s.iconColor }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-3xl font-black truncate">{s.value}</p>
                  <p className="text-xs sm:text-sm truncate" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto gap-2 mb-6 sm:mb-8 border-b pb-3 sm:pb-4 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide" style={{ borderColor: 'var(--border-main)', WebkitOverflowScrolling: 'touch' }}>
            {[
              { id: 'users', icon: Users, label: 'Users' },
              { id: 'submissions', icon: Globe, label: 'Submissions' },
              { id: 'credits', icon: Receipt, label: 'Credit History' },
              { id: 'purchases', icon: ShoppingCart, label: 'Purchase History' },
              { id: 'inr_payments', icon: CurrencyInr, label: `INR Payments${inrPayments.filter(p => p.status === 'pending').length > 0 ? ` (${inrPayments.filter(p => p.status === 'pending').length})` : ''}` },
              { id: 'apis', icon: Plugs, label: 'Indexing APIs' },
              { id: 'upi_settings', icon: Gear, label: 'UPI Settings' },
              { id: 'coupons', icon: Receipt, label: 'Coupons' },
              { id: 'custom_packages', icon: Lightning, label: 'Custom Pkgs' },
              { id: 'pay_settings', icon: CreditCard, label: 'Pay Settings' },
              { id: 'activity_log', icon: ClockCounterClockwise, label: 'Activity Log' },
              { id: 'api_system', icon: Globe, label: 'API System' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-medium transition-all rounded-lg text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                style={tabStyle(activeTab === tab.id)}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 sm:w-5 sm:h-5" /> {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner className="w-10 h-10 animate-spin" style={{ color: 'var(--accent)' }} />
            </div>
          ) : (
            <>
              {/* USERS TAB */}
              {activeTab === 'users' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-xl font-bold">Users ({filteredUsers.length})</h2>
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <div className="relative flex-1 sm:flex-initial">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                        <input type="text" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} placeholder="Search by email..." className="input-field pl-10 pr-10 w-full sm:w-64" data-testid="search-email-input" />
                        {searchEmail && <button onClick={() => setSearchEmail('')} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}><X className="w-4 h-4" /></button>}
                      </div>
                      <button onClick={fetchData} className="transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="refresh-users"><ArrowClockwise className="w-5 h-5" /></button>
                      <button onClick={() => handleExport('users')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} data-testid="export-users"><DownloadSimple className="w-3.5 h-3.5" /> CSV</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px] text-left text-sm">
                      <thead><tr className="table-header"><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Credits</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Actions</th></tr></thead>
                      <tbody>
                        {filteredUsers.map((userItem, index) => (
                          <tr key={userItem._id} className="table-row" data-testid={`user-row-${index}`}>
                            <td className="px-4 table-cell"><div><p className="font-medium">{userItem.name}</p><p className="text-xs" style={{ color: 'var(--text-muted)' }}>{userItem.email}</p></div></td>
                            <td className="px-4 table-cell">
                              <span className="px-2 py-1 text-xs font-medium rounded" style={userItem.role === 'admin' ? { background: 'var(--accent-bg)', color: 'var(--accent)' } : { background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>
                                {userItem.role}
                              </span>
                            </td>
                            <td className="px-4 table-cell font-mono font-bold">{userItem.credits}</td>
                            <td className="px-4 table-cell" style={{ color: 'var(--text-muted)' }}>{new Date(userItem.created_at).toLocaleDateString()}</td>
                            <td className="px-4 table-cell">
                              <div className="flex items-center gap-1">
                                <button onClick={() => openUserDetailModal(userItem)} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--accent)' }} title="View" data-testid={`view-user-${index}`}><Eye className="w-4 h-4" /></button>
                                <button onClick={() => openCreditModal(userItem, 'add')} className="p-2 text-emerald-400 hover:bg-emerald-400/10 transition-colors rounded-lg" title="Add credits" data-testid={`add-credits-${index}`}><Plus className="w-4 h-4" /></button>
                                <button onClick={() => openCreditModal(userItem, 'subtract')} className="p-2 text-amber-400 hover:bg-amber-400/10 transition-colors rounded-lg" title="Subtract" data-testid={`subtract-credits-${index}`}><Minus className="w-4 h-4" /></button>
                                {userItem.role !== 'admin' && <button onClick={() => handleDeleteUser(userItem._id)} className="p-2 text-red-400 hover:bg-red-400/10 transition-colors rounded-lg" title="Delete" data-testid={`delete-user-${index}`}><Trash className="w-4 h-4" /></button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredUsers.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center" style={{ color: 'var(--text-muted)' }}>{searchEmail ? 'No users found matching your search' : 'No users found'}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SUBMISSIONS TAB */}
              {activeTab === 'submissions' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-3 sm:p-4 border-b flex flex-wrap items-center justify-between gap-2" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-base sm:text-xl font-bold">Submissions ({submissions.length})</h2>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleExport('submissions')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} data-testid="export-submissions"><DownloadSimple className="w-3.5 h-3.5" /> CSV</button>
                    <button onClick={fetchData} className="flex items-center gap-2 transition-colors text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refresh-submissions"><ArrowClockwise className="w-5 h-5" /> Refresh</button>
                    </div>
                  </div>
                  {submissions.length === 0 ? (
                    <div className="p-12 text-center"><Globe weight="duotone" className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-dimmed)' }} /><p style={{ color: 'var(--text-muted)' }}>No submissions yet</p></div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                      {submissions.map((sub, index) => (
                        <div key={sub.id} className="p-3 sm:p-4" data-testid={`admin-submission-row-${index}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0">
                              <div className="min-w-0">
                                <p className="font-medium text-xs sm:text-sm">{new Date(sub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                <p className="text-xs truncate max-w-[180px] sm:max-w-none" style={{ color: 'var(--accent)' }}>{sub.user_email || getUserEmail(sub.user_id)}</p>
                              </div>
                              <span className="badge-processing text-[10px] sm:text-xs">{sub.urls.length} URLs</span>
                              <span className={`px-2 py-0.5 text-[10px] sm:text-xs rounded ${sub.status === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{sub.status}</span>
                            </div>
                            <div className="flex items-center gap-1 self-end sm:self-auto flex-shrink-0">
                              <button onClick={() => copyAllUrls(sub.urls, index)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }} title="Copy all URLs">
                                {copiedId === `all-${index}` ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                              </button>
                              <button onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }} title="View URLs">
                                {expandedSubmission === sub.id ? <CaretUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          {expandedSubmission === sub.id && (
                            <div className="mt-3 rounded-lg p-2 sm:p-4 max-h-64 overflow-y-auto" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-main)' }}>
                              <div className="space-y-1.5">
                                {sub.urls.map((url, i) => (
                                  <div key={i} className="flex items-center gap-2 p-1.5 sm:p-2 rounded" style={{ background: 'var(--bg-card)' }}>
                                    <p className="font-mono text-[10px] sm:text-xs truncate flex-1 min-w-0" style={{ color: 'var(--text-secondary)' }}>{url}</p>
                                    <div className="flex items-center gap-0.5 flex-shrink-0">
                                      <button onClick={() => copyUrl(url, `${sub.id}-${i}`)} className="p-1 sm:p-1.5 rounded hover:bg-white/10 transition-colors" style={{ color: 'var(--text-muted)' }} title="Copy URL" data-testid={`copy-url-${index}-${i}`}>
                                        {copiedId === `${sub.id}-${i}` ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                      </button>
                                      <a href={`https://www.google.com/search?q=site:${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="p-1 sm:p-1.5 rounded hover:bg-white/10 transition-colors" style={{ color: 'var(--accent)' }} title="Check on Google (site:URL)" data-testid={`check-url-${index}-${i}`}>
                                        <MagnifyingGlass className="w-3.5 h-3.5" />
                                      </a>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* CREDITS TAB */}
              {activeTab === 'credits' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-xl font-bold">All Credit History</h2>
                    <button onClick={() => handleExport('credit-history')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} data-testid="export-credit-history"><DownloadSimple className="w-3.5 h-3.5" /> CSV</button>
                    <button onClick={fetchData} className="flex items-center gap-2 transition-colors text-sm" style={{ color: 'var(--text-secondary)' }}><ArrowClockwise className="w-5 h-5" /> Refresh</button>
                  </div>
                  {creditHistory.length === 0 ? (
                    <div className="p-12 text-center"><Receipt weight="duotone" className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-dimmed)' }} /><p style={{ color: 'var(--text-muted)' }}>No credit history yet</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-left text-sm">
                        <thead><tr className="table-header"><th className="px-4 py-3">Date</th><th className="px-4 py-3">User Email</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Amount</th></tr></thead>
                        <tbody>
                          {creditHistory.map((entry, index) => (
                            <tr key={index} className="table-row">
                              <td className="px-4 table-cell text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                              <td className="px-4 table-cell"><span className="text-xs">{entry.user_email || entry.user_id}</span></td>
                              <td className="px-4 table-cell">
                                <span className={`px-2 py-1 text-xs rounded ${entry.type === 'purchase' ? 'bg-emerald-500/20 text-emerald-400' : entry.type === 'admin_add' ? 'bg-blue-500/20 text-blue-400' : entry.type === 'admin_subtract' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'}`}>
                                  {entry.type.replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-4 table-cell text-xs" style={{ color: 'var(--text-secondary)' }}>{entry.description}</td>
                              <td className={`px-4 table-cell font-bold ${entry.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{entry.amount > 0 ? '+' : ''}{entry.amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* PURCHASES TAB */}
              {activeTab === 'purchases' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-xl font-bold">All Purchase History</h2>
                    <button onClick={fetchData} className="flex items-center gap-2 transition-colors text-sm" style={{ color: 'var(--text-secondary)' }}><ArrowClockwise className="w-5 h-5" /> Refresh</button>
                  </div>
                  {purchaseHistory.length === 0 ? (
                    <div className="p-12 text-center"><ShoppingCart weight="duotone" className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-dimmed)' }} /><p style={{ color: 'var(--text-muted)' }}>No purchases yet</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-left text-sm">
                        <thead><tr className="table-header"><th className="px-4 py-3">Date & Time</th><th className="px-4 py-3">User Email</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Credits</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th></tr></thead>
                        <tbody>
                          {purchaseHistory.map((purchase, index) => (
                            <tr key={index} className="table-row">
                              <td className="px-4 table-cell"><div><p className="font-medium text-xs">{new Date(purchase.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p><p className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(purchase.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p></div></td>
                              <td className="px-4 table-cell"><span className="text-xs">{purchase.user_email || getUserEmail(purchase.user_id)}</span></td>
                              <td className="px-4 table-cell capitalize">{purchase.plan_id || 'N/A'}</td>
                              <td className="px-4 table-cell font-mono font-bold">+{purchase.credits}</td>
                              <td className="px-4 table-cell" style={{ color: 'var(--text-secondary)' }}>{purchase.pay_amount} {purchase.pay_currency} (${purchase.price_amount})</td>
                              <td className="px-4 table-cell">{getPaymentStatusBadge(purchase.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}


              {/* INR PAYMENTS TAB */}
              {activeTab === 'inr_payments' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <CurrencyInr weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} /> INR Payments
                    </h2>
                    <button onClick={fetchData} className="flex items-center gap-2 transition-colors text-sm" style={{ color: 'var(--text-secondary)' }}><ArrowClockwise className="w-5 h-5" /> Refresh</button>
                    <button onClick={() => handleExport('inr-payments')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} data-testid="export-inr-payments"><DownloadSimple className="w-3.5 h-3.5" /> CSV</button>
                  </div>
                  {inrPayments.length === 0 ? (
                    <div className="p-12 text-center"><CurrencyInr weight="duotone" className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-dimmed)' }} /><p style={{ color: 'var(--text-muted)' }}>No INR payments yet</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-left text-sm">
                        <thead><tr className="table-header"><th className="px-4 py-3">Date</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">UTR</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
                        <tbody>
                          {inrPayments.map((payment, index) => (
                            <tr key={payment._id} className="table-row" data-testid={`inr-payment-row-${index}`}>
                              <td className="px-4 table-cell text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(payment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                              <td className="px-4 table-cell text-xs">{payment.user_email}</td>
                              <td className="px-4 table-cell font-medium">{payment.plan_name}<br/><span className="text-xs" style={{ color: 'var(--text-muted)' }}>{(payment.credits || 0).toLocaleString()} credits</span></td>
                              <td className="px-4 table-cell font-bold">₹{(payment.amount_inr || 0).toLocaleString()}</td>
                              <td className="px-4 table-cell font-mono text-xs">{payment.utr_number}</td>
                              <td className="px-4 table-cell">{getInrStatusBadge(payment.status)}</td>
                              <td className="px-4 table-cell">
                                {payment.status === 'pending' ? (
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => handleApproveInr(payment._id)} className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors" title="Approve" data-testid={`approve-inr-${index}`}><Check weight="bold" className="w-4 h-4" /></button>
                                    <button onClick={() => handleRejectInr(payment._id)} className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" title="Reject" data-testid={`reject-inr-${index}`}><XCircle weight="bold" className="w-4 h-4" /></button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => viewReceipt('inr', payment._id)} className="p-1.5 rounded-lg transition-colors" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} title="View Receipt"><Receipt weight="bold" className="w-4 h-4" /></button>
                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{payment.approved_by || payment.rejected_by || '-'}</span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* UPI SETTINGS TAB */}
              {activeTab === 'upi_settings' && (
                <div className="max-w-lg">
                  <div className="border rounded-xl p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <h2 className="text-xl font-bold mb-5 flex items-center gap-2">
                      <Bank weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} /> UPI Payment Settings
                    </h2>
                    <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
                      Configure the UPI ID that users will see when making INR payments.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>UPI ID</label>
                        <input
                          type="text"
                          value={upiSettings.upi_id}
                          onChange={e => setUpiSettings(prev => ({ ...prev, upi_id: e.target.value }))}
                          className="input-field text-sm font-mono"
                          placeholder="yourname@paytm"
                          data-testid="upi-id-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Display Name</label>
                        <input
                          type="text"
                          value={upiSettings.display_name}
                          onChange={e => setUpiSettings(prev => ({ ...prev, display_name: e.target.value }))}
                          className="input-field text-sm"
                          placeholder="Your Business Name"
                          data-testid="upi-name-input"
                        />
                      </div>
                      <button
                        onClick={handleSaveUpi}
                        disabled={savingUpi || !upiSettings.upi_id.trim()}
                        className="btn-primary w-full py-3 flex items-center justify-center gap-2 font-bold disabled:opacity-50"
                        data-testid="save-upi-btn"
                      >
                        {savingUpi ? <Spinner className="w-4 h-4 animate-spin" /> : <CheckCircle weight="bold" className="w-4 h-4" />}
                        Save UPI Settings
                      </button>
                    </div>
                    {upiSettings.upi_id && (
                      <div className="mt-5 p-4 rounded-lg" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-main)' }}>
                        <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Current UPI ID shown to users:</p>
                        <p className="font-mono font-bold text-sm" style={{ color: 'var(--accent)' }}>{upiSettings.upi_id}</p>
                        {upiSettings.display_name && <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Name: {upiSettings.display_name}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}



              {/* COUPONS TAB */}
              {activeTab === 'coupons' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-lg sm:text-xl font-bold">Discount Coupons</h2>
                    <button onClick={() => openCouponModal()} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1" data-testid="add-coupon-btn"><Plus className="w-4 h-4" /> New Coupon</button>
                  </div>
                  {coupons.length === 0 ? (
                    <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>No coupons yet</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-left text-sm">
                        <thead><tr className="border-b text-xs uppercase" style={{ borderColor: 'var(--border-main)', color: 'var(--text-muted)' }}>
                          <th className="p-3">Code</th><th className="p-3">Type</th><th className="p-3">Value</th><th className="p-3">Uses</th><th className="p-3">Status</th><th className="p-3">Actions</th>
                        </tr></thead>
                        <tbody>
                          {coupons.map(c => (
                            <tr key={c._id} className="border-b" style={{ borderColor: 'var(--border-main)' }}>
                              <td className="p-3 font-mono font-bold">{c.code}</td>
                              <td className="p-3 capitalize">{c.discount_type}</td>
                              <td className="p-3">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`}</td>
                              <td className="p-3">{c.times_used || 0}{c.max_uses ? ` / ${c.max_uses}` : ' / Unlimited'}</td>
                              <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                              <td className="p-3 flex items-center gap-2">
                                <button onClick={() => openCouponModal(c)} className="p-1 rounded hover:bg-white/10" data-testid={`edit-coupon-${c._id}`}><PencilSimple className="w-4 h-4" /></button>
                                <button onClick={() => deleteCoupon(c._id)} className="p-1 rounded hover:bg-red-500/10 text-red-400" data-testid={`delete-coupon-${c._id}`}><Trash className="w-4 h-4" /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* CUSTOM PACKAGES TAB */}
              {activeTab === 'custom_packages' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-lg sm:text-xl font-bold">Custom Packages</h2>
                    <button onClick={() => setShowCustomPkgModal(true)} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1" data-testid="add-custom-pkg-btn"><Plus className="w-4 h-4" /> New Package</button>
                  </div>
                  {customPackages.length === 0 ? (
                    <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>No custom packages yet</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-left text-sm">
                        <thead><tr className="border-b text-xs uppercase" style={{ borderColor: 'var(--border-main)', color: 'var(--text-muted)' }}>
                          <th className="p-3">User</th><th className="p-3">Name</th><th className="p-3">Credits</th><th className="p-3">Price</th><th className="p-3">Created</th><th className="p-3">Actions</th>
                        </tr></thead>
                        <tbody>
                          {customPackages.map(p => (
                            <tr key={p._id} className="border-b" style={{ borderColor: 'var(--border-main)' }}>
                              <td className="p-3 text-xs">{p.user_email}</td>
                              <td className="p-3 font-bold">{p.name}</td>
                              <td className="p-3">{p.credits?.toLocaleString()}</td>
                              <td className="p-3 font-bold">${p.price_usd}</td>
                              <td className="p-3 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                              <td className="p-3"><button onClick={() => deleteCustomPackage(p._id)} className="p-1 rounded hover:bg-red-500/10 text-red-400"><Trash className="w-4 h-4" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* PAYMENT SETTINGS TAB */}
              {activeTab === 'pay_settings' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-bold mb-6">Payment Methods</h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg" style={{ borderColor: 'var(--border-main)', background: 'var(--bg-input)' }}>
                        <div>
                          <p className="font-bold">Cryptocurrency</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>NOWPayments (BTC, ETH, USDT, etc.)</p>
                        </div>
                        <button
                          onClick={() => setPaymentSettings(prev => ({ ...prev, crypto_enabled: !prev.crypto_enabled }))}
                          className={`w-12 h-6 rounded-full transition-colors relative ${paymentSettings.crypto_enabled ? 'bg-emerald-500' : 'bg-gray-500'}`}
                          data-testid="toggle-crypto"
                        >
                          <span className={`block w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${paymentSettings.crypto_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg" style={{ borderColor: 'var(--border-main)', background: 'var(--bg-input)' }}>
                        <div>
                          <p className="font-bold">INR (UPI)</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Manual UPI payment with UTR verification</p>
                        </div>
                        <button
                          onClick={() => setPaymentSettings(prev => ({ ...prev, inr_enabled: !prev.inr_enabled }))}
                          className={`w-12 h-6 rounded-full transition-colors relative ${paymentSettings.inr_enabled ? 'bg-emerald-500' : 'bg-gray-500'}`}
                          data-testid="toggle-inr"
                        >
                          <span className={`block w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${paymentSettings.inr_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    </div>
                    <button onClick={savePaymentSettings} disabled={savingPaySettings} className="btn-primary mt-6 px-6 py-2.5" data-testid="save-pay-settings">
                      {savingPaySettings ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}


              {/* ACTIVITY LOG TAB */}
              {activeTab === 'activity_log' && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <ClockCounterClockwise weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} /> Activity Log
                    </h2>
                    <button onClick={fetchData} className="flex items-center gap-2 transition-colors text-sm" style={{ color: 'var(--text-secondary)' }}><ArrowClockwise className="w-5 h-5" /> Refresh</button>
                  </div>
                  {activityLog.length === 0 ? (
                    <div className="p-12 text-center"><ClockCounterClockwise weight="duotone" className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-dimmed)' }} /><p style={{ color: 'var(--text-muted)' }}>No admin activity recorded yet</p></div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                      {activityLog.map((log, i) => (
                        <div key={log._id} className="p-4 flex items-start gap-3" data-testid={`activity-row-${i}`}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>
                            {getActivityIcon(log.action)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{log.details}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>{log.action}</span>
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{log.admin_email}</span>
                              <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>{new Date(log.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}



              {/* INDEXING APIS TAB */}
              {activeTab === 'apis' && (
                <div className="space-y-4">
                  <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                      <h2 className="text-xl font-bold flex items-center gap-2"><Plugs weight="duotone" className="w-6 h-6" style={{ color: 'var(--accent)' }} /> Indexing API Management</h2>
                      <div className="flex items-center gap-2">
                        <button onClick={fetchApis} className="flex items-center gap-2 transition-colors text-sm" style={{ color: 'var(--text-secondary)' }}><ArrowClockwise className="w-4 h-4" /> Refresh</button>
                        <button onClick={openAddApi} className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5" data-testid="add-api-btn"><Plus className="w-4 h-4" /> Add API</button>
                      </div>
                    </div>
                    <div className="p-4 text-xs border-b" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-main)' }}>Only one API can be active at a time. Activating one will automatically deactivate others.</div>
                    {indexingApis.length === 0 ? (
                      <div className="p-12 text-center"><Plugs weight="duotone" className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-dimmed)' }} /><p style={{ color: 'var(--text-muted)' }}>No indexing APIs configured</p><p className="text-sm mt-1" style={{ color: 'var(--text-dimmed)' }}>Add your first indexing API to start</p></div>
                    ) : (
                      <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                        {indexingApis.map((api) => (
                          <div key={api._id} className="p-4 transition-colors" style={api.is_active ? { background: 'rgba(16,185,129,0.04)' } : {}} data-testid={`api-row-${api._id}`}>
                            <div className="flex items-start sm:items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-bold text-sm">{api.name}</h3>
                                  {api.is_active ? (
                                    <span className="px-2 py-0.5 text-[10px] font-bold bg-emerald-500/20 text-emerald-400 rounded-full flex items-center gap-1" data-testid={`api-active-badge-${api._id}`}><Power weight="fill" className="w-3 h-3" /> ACTIVE</span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-[10px] font-medium rounded-full" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}>INACTIVE</span>
                                  )}
                                </div>
                                <p className="text-xs font-mono truncate" style={{ color: 'var(--text-secondary)' }}>{api.base_url}</p>
                                <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-dimmed)' }}>{api.api_key.slice(0, 12)}{'*'.repeat(20)}{api.api_key.slice(-6)}</p>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {!api.is_active && (
                                  <button onClick={() => handleActivateApi(api._id)} className="px-3 py-1.5 text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-1" data-testid={`activate-api-${api._id}`}><Power className="w-3.5 h-3.5" /> Activate</button>
                                )}
                                <button onClick={() => openEditApi(api)} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }} data-testid={`edit-api-${api._id}`} title="Edit"><PencilSimple className="w-4 h-4" /></button>
                                {!api.is_active && <button onClick={() => handleDeleteApi(api._id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid={`delete-api-${api._id}`} title="Delete"><Trash className="w-4 h-4" /></button>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

              {/* API SYSTEM TAB */}
              {activeTab === 'api_system' && (
                <div className="space-y-4 sm:space-y-6">
                  {/* API Key Card */}
                  <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <div className="p-4 sm:p-6 border-b" style={{ borderColor: 'var(--border-main)' }}>
                      <h2 className="text-lg sm:text-xl font-bold mb-1">Master API Key</h2>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>This key is required for all public API calls. Only visible to admin.</p>
                    </div>
                    <div className="p-4 sm:p-6">
                      {masterApiKey?.key ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 px-3 py-2.5 rounded-lg border font-mono text-xs sm:text-sm overflow-hidden" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}>
                              {showApiKey ? masterApiKey.key : '••••••••••••••••••••••••••••••••••••••'}
                            </div>
                            <button onClick={() => setShowApiKey(!showApiKey)} className="p-2 rounded-lg border" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }} data-testid="toggle-show-key">
                              {showApiKey ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                            <button onClick={() => copyToClipboard(masterApiKey.key)} className="p-2 rounded-lg border" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }} data-testid="copy-api-key">
                              <Copy className="w-5 h-5" />
                            </button>
                          </div>
                          {masterApiKey.created_at && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Created: {new Date(masterApiKey.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</p>}
                          {masterApiKey.last_used && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Last used: {new Date(masterApiKey.last_used).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</p>}
                          <div className="flex gap-2 pt-2">
                            <button onClick={generateMasterKey} disabled={generatingKey} className="btn-primary text-xs px-4 py-2" data-testid="regenerate-key-btn">{generatingKey ? 'Generating...' : 'Regenerate Key'}</button>
                            <button onClick={revokeMasterKey} className="px-4 py-2 rounded-lg text-xs font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors" data-testid="revoke-key-btn">Revoke Key</button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>No API key generated yet</p>
                          <button onClick={generateMasterKey} disabled={generatingKey} className="btn-primary px-6 py-2.5" data-testid="generate-key-btn">{generatingKey ? 'Generating...' : 'Generate API Key'}</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* API Endpoints Documentation */}
                  <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <div className="p-4 sm:p-6 border-b" style={{ borderColor: 'var(--border-main)' }}>
                      <h2 className="text-lg sm:text-xl font-bold mb-1">API Endpoints</h2>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Base URL: <span className="font-mono" style={{ color: 'var(--accent)' }}>{window.location.origin}/api/v1</span></p>
                    </div>
                    <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                      {[
                        { method: 'POST', path: '/auth/login', desc: 'Login and get Bearer token', auth: 'X-API-Key', body: '{"email":"...","password":"..."}' },
                        { method: 'GET', path: '/credits', desc: 'Get user credit balance', auth: 'X-API-Key + Bearer' },
                        { method: 'POST', path: '/submit', desc: 'Submit URLs for indexing', auth: 'X-API-Key + Bearer', body: '{"urls":["url1","url2"]}' },
                        { method: 'GET', path: '/history', desc: 'Get submission history', auth: 'X-API-Key + Bearer' },
                        { method: 'GET', path: '/submission/{id}', desc: 'Get submission details', auth: 'X-API-Key + Bearer' },
                        { method: 'GET', path: '/admin/users', desc: 'List all users (admin)', auth: 'X-API-Key + Admin Bearer' },
                        { method: 'POST', path: '/admin/credits/add', desc: 'Add credits (admin)', auth: 'X-API-Key + Admin Bearer', body: '{"user_email":"...","amount":100}' },
                        { method: 'POST', path: '/admin/credits/subtract', desc: 'Subtract credits (admin)', auth: 'X-API-Key + Admin Bearer', body: '{"user_email":"...","amount":50}' },
                        { method: 'GET', path: '/admin/user/{email}/credits', desc: 'Get user credits (admin)', auth: 'X-API-Key + Admin Bearer' },
                      ].map((ep, i) => (
                        <div key={i} className="p-3 sm:p-4">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className={`text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded ${ep.method === 'GET' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{ep.method}</span>
                            <code className="font-mono text-xs sm:text-sm" style={{ color: 'var(--accent)' }}>/api/v1{ep.path}</code>
                          </div>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{ep.desc}</p>
                          <p className="text-[10px] mt-1" style={{ color: 'var(--text-dimmed)' }}>Auth: {ep.auth}</p>
                          {ep.body && <code className="block text-[10px] sm:text-xs mt-1 font-mono px-2 py-1 rounded" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>{ep.body}</code>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Usage Example */}
                  <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <div className="p-4 sm:p-6 border-b" style={{ borderColor: 'var(--border-main)' }}>
                      <h2 className="text-lg sm:text-xl font-bold">Quick Start</h2>
                    </div>
                    <div className="p-4 sm:p-6">
                      <pre className="text-[10px] sm:text-xs font-mono overflow-x-auto p-3 sm:p-4 rounded-lg" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>{`# 1. Login
curl -X POST ${window.location.origin}/api/v1/auth/login \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"user@example.com","password":"pass"}'

# 2. Check Credits  
curl ${window.location.origin}/api/v1/credits \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Authorization: Bearer TOKEN"

# 3. Submit URLs
curl -X POST ${window.location.origin}/api/v1/submit \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Authorization: Bearer TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"urls":["https://example.com/page1"]}'`}</pre>
                    </div>
                  </div>
                </div>
              )}


        </div>
      </main>

      {/* Add/Edit API Modal */}
      {showAddApiModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="api-modal">
          <div className="border w-full max-w-md p-6 rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold">{editingApi ? 'Edit API' : 'Add Indexing API'}</h3>
              <button onClick={() => { setShowAddApiModal(false); setEditingApi(null); }} className="p-1" style={{ color: 'var(--text-muted)' }}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>API Name</label><input type="text" value={apiForm.name} onChange={e => setApiForm(prev => ({ ...prev, name: e.target.value }))} className="input-field text-sm" placeholder="e.g. 2MinuteIndex, RocketIndexer" data-testid="api-name-input" /></div>
              <div><label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Base URL</label><input type="text" value={apiForm.base_url} onChange={e => setApiForm(prev => ({ ...prev, base_url: e.target.value }))} className="input-field text-sm font-mono" placeholder="https://2minuteindex.com/api" data-testid="api-url-input" /></div>
              <div><label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>API Key</label><input type="text" value={apiForm.api_key} onChange={e => setApiForm(prev => ({ ...prev, api_key: e.target.value }))} className="input-field text-sm font-mono" placeholder="Your API key" data-testid="api-key-input" /></div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setShowAddApiModal(false); setEditingApi(null); }} className="flex-1 py-2.5 border rounded-lg transition-colors text-sm" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }}>Cancel</button>
                <button onClick={handleSaveApi} disabled={savingApi || !apiForm.name || !apiForm.base_url || !apiForm.api_key} className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 text-sm" data-testid="save-api-btn">
                  {savingApi ? <Spinner className="w-4 h-4 animate-spin" /> : <CheckCircle weight="bold" className="w-4 h-4" />}
                  {editingApi ? 'Update' : 'Add API'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="credit-modal">
          <div className="border w-full max-w-md p-6 rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <h3 className="text-xl font-bold mb-4">{creditAction === 'add' ? 'Add' : 'Subtract'} Credits</h3>
            <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>User: <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedUser?.name}</span></p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Email: <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedUser?.email}</span></p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Current credits: <span className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{selectedUser?.credits}</span></p>
            <div className="mb-4">
              <label className="text-sm font-medium block mb-2" style={{ color: 'var(--text-secondary)' }}>Amount</label>
              <input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="input-field" placeholder="Enter amount" min="1" data-testid="credit-amount-input" />
            </div>
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-5 h-5 rounded" style={{ accentColor: 'var(--accent)' }} />
                <span className="text-sm flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><Envelope className="w-4 h-4" /> Send email notification to user</span>
              </label>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowCreditModal(false)} className="flex-1 btn-secondary" data-testid="cancel-credit-modal">Cancel</button>
              <button onClick={handleCreditUpdate} className={`flex-1 font-bold px-6 py-3 transition-all flex items-center justify-center gap-2 rounded-lg ${creditAction === 'add' ? 'bg-emerald-500 hover:bg-emerald-400 text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'}`} data-testid="confirm-credit-update">
                {creditAction === 'add' ? <Plus className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                {creditAction === 'add' ? 'Add' : 'Subtract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserDetailModal && userDetailData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="user-detail-modal">
          <div className="border w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl flex flex-col" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <div className="p-4 sm:p-6 border-b flex items-center justify-between gap-3" style={{ borderColor: 'var(--border-main)' }}>
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold truncate">{userDetailData.name}</h3>
                <p className="text-xs sm:text-sm truncate" style={{ color: 'var(--accent)' }}>{userDetailData.email}</p>
              </div>
              <button onClick={() => setShowUserDetailModal(false)} className="p-2 transition-colors" style={{ color: 'var(--text-muted)' }}><X className="w-6 h-6" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
                {[
                  { value: userDetailData.credits, label: 'Credits' },
                  { value: userDetailData.role, label: 'Role' },
                  { value: new Date(userDetailData.created_at).toLocaleDateString(), label: 'Joined' },
                ].map((item, i) => (
                  <div key={i} className="border p-3 sm:p-4 rounded-lg text-center" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }}>
                    <p className="text-lg sm:text-2xl font-black truncate">{item.value}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                  </div>
                ))}
              </div>
              {loadingUserDetail ? (
                <div className="text-center py-8"><Spinner className="w-8 h-8 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
              ) : (
                <>
                  <div className="mb-6">
                    <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><Receipt className="w-4 h-4" style={{ color: 'var(--accent)' }} /> Credit History</h4>
                    {userCreditHistory.length === 0 ? <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No credit history</p> : (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {userCreditHistory.map((entry, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg text-sm" style={{ background: 'var(--bg-input)' }}>
                            <div><p style={{ color: 'var(--text-secondary)' }}>{entry.description}</p><p className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p></div>
                            <span className={`font-bold ${entry.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{entry.amount > 0 ? '+' : ''}{entry.amount}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><ShoppingCart className="w-4 h-4" style={{ color: 'var(--accent)' }} /> Purchase History</h4>
                    {userPurchaseHistory.length === 0 ? <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No purchases</p> : (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {userPurchaseHistory.map((purchase, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg text-sm" style={{ background: 'var(--bg-input)' }}>
                            <div><p className="capitalize" style={{ color: 'var(--text-secondary)' }}>{purchase.plan_id} - {purchase.credits} credits</p><p className="text-xs" style={{ color: 'var(--text-muted)' }}>{purchase.pay_amount} {purchase.pay_currency} • {new Date(purchase.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p></div>
                            {getPaymentStatusBadge(purchase.status)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* URL History */}
              <div className="mt-4">
                <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Globe className="w-4 h-4" /> Submitted URLs ({userUrls.length})</h4>
                {userUrls.length === 0 ? (
                  <p className="text-xs py-3" style={{ color: 'var(--text-muted)' }}>No URLs submitted</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {userUrls.map((u, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded text-xs border" style={{ borderColor: 'var(--border-main)', background: 'var(--bg-input)' }}>
                        <span className="truncate flex-1 font-mono">{u.url}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : u.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{u.status}</span>
                          <a href={u.site_check_url} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-white/10" title="Check on Google" data-testid={`check-url-${i}`}><MagnifyingGlass className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} /></a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t flex gap-3" style={{ borderColor: 'var(--border-main)' }}>
              <button onClick={() => { setShowUserDetailModal(false); openCreditModal(userDetailData, 'add'); }} className="flex-1 btn-primary flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Add Credits</button>
              <button onClick={() => { setShowUserDetailModal(false); openCreditModal(userDetailData, 'subtract'); }} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-lg flex items-center justify-center gap-2"><Minus className="w-5 h-5" /> Subtract Credits</button>
            </div>
          </div>
        </div>
      )}

      {/* Send Credits Modal */}
      {showSendCreditsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="send-credits-modal">
          <div className="border w-full max-w-md p-6 rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold flex items-center gap-2"><PaperPlaneTilt weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} /> Send Credits</h3>
              <button onClick={() => setShowSendCreditsModal(false)} className="p-1" style={{ color: 'var(--text-muted)' }}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>User Email</label>
                <input type="email" value={sendCreditsForm.user_email} onChange={e => setSendCreditsForm(prev => ({ ...prev, user_email: e.target.value }))} className="input-field text-sm" placeholder="user@example.com" data-testid="send-credits-email" />
              </div>
              <div>
                <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Credits Amount</label>
                <input type="number" value={sendCreditsForm.amount} onChange={e => setSendCreditsForm(prev => ({ ...prev, amount: e.target.value }))} className="input-field text-sm" placeholder="1000" min="1" data-testid="send-credits-amount" />
              </div>
              <div>
                <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Note (optional)</label>
                <input type="text" value={sendCreditsForm.note} onChange={e => setSendCreditsForm(prev => ({ ...prev, note: e.target.value }))} className="input-field text-sm" placeholder="e.g. Bonus credits" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={sendCreditsForm.send_email} onChange={e => setSendCreditsForm(prev => ({ ...prev, send_email: e.target.checked }))} className="w-5 h-5 rounded" style={{ accentColor: 'var(--accent)' }} />
                <span className="text-sm flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><Envelope className="w-4 h-4" /> Send email notification</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowSendCreditsModal(false)} className="flex-1 py-2.5 border rounded-lg text-sm" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }}>Cancel</button>
                <button onClick={handleSendCredits} disabled={sendingCredits || !sendCreditsForm.user_email || !sendCreditsForm.amount} className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-bold" data-testid="confirm-send-credits">
                  {sendingCredits ? <Spinner className="w-4 h-4 animate-spin" /> : <PaperPlaneTilt weight="fill" className="w-4 h-4" />}
                  Send Credits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="receipt-modal">
          <div className="border w-full max-w-lg rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
              <h3 className="text-lg font-bold flex items-center gap-2"><Receipt weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} /> Payment Receipt</h3>
              <button onClick={() => setShowReceiptModal(false)} className="p-1" style={{ color: 'var(--text-muted)' }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3" id="receipt-content">
              <div className="text-center mb-4">
                <p className="text-2xl font-black" style={{ color: 'var(--accent)' }}>{receiptData.site_name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Payment Receipt</p>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">Type</span>
                <span className="font-bold text-sm">{receiptData.receipt_type === 'inr' ? 'INR (UPI)' : 'Crypto'}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">User</span>
                <span className="text-sm">{receiptData.user_email}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">Plan</span>
                <span className="font-bold text-sm">{receiptData.plan}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">Credits</span>
                <span className="font-bold text-sm" style={{ color: 'var(--accent)' }}>{(receiptData.credits || 0).toLocaleString()}</span>
              </div>
              {receiptData.receipt_type === 'inr' ? (
                <>
                  <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                    <span style={{ color: 'var(--text-muted)' }} className="text-sm">Amount</span>
                    <span className="font-bold text-sm">₹{(receiptData.amount_inr || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                    <span style={{ color: 'var(--text-muted)' }} className="text-sm">UTR Number</span>
                    <span className="font-mono text-sm">{receiptData.utr_number}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                    <span style={{ color: 'var(--text-muted)' }} className="text-sm">Amount</span>
                    <span className="font-bold text-sm">${receiptData.amount_usd} ({receiptData.pay_amount} {receiptData.pay_currency})</span>
                  </div>
                </>
              )}
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">Status</span>
                <span className={`text-sm font-bold ${receiptData.status === 'approved' || receiptData.status === 'finished' ? 'text-emerald-400' : receiptData.status === 'rejected' ? 'text-red-400' : 'text-amber-400'}`}>{receiptData.status?.toUpperCase()}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">Date</span>
                <span className="text-sm">{receiptData.created_at ? new Date(receiptData.created_at).toLocaleString() : '-'}</span>
              </div>
              {receiptData.approved_by && (
                <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-main)' }}>
                  <span style={{ color: 'var(--text-muted)' }} className="text-sm">Approved By</span>
                  <span className="text-sm">{receiptData.approved_by}</span>
                </div>
              )}
              <div className="flex justify-between py-2">
                <span style={{ color: 'var(--text-muted)' }} className="text-sm">Receipt ID</span>
                <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{receiptData.payment_id}</span>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border-main)' }}>
              <button onClick={() => { window.print(); }} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }} data-testid="print-receipt"><DownloadSimple className="w-4 h-4" /> Print</button>
              <button onClick={() => setShowReceiptModal(false)} className="btn-primary px-4 py-2 text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* COUPON MODAL */}
      {showCouponModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="border w-full max-w-md rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <div className="p-4 sm:p-6 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
              <h3 className="text-lg font-bold">{editingCoupon ? 'Edit Coupon' : 'New Coupon'}</h3>
              <button onClick={() => setShowCouponModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Code</label>
                <input value={couponForm.code} onChange={(e) => setCouponForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} className="w-full px-3 py-2 rounded-lg border text-sm font-mono" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="SAVE20" data-testid="coupon-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Type</label>
                  <select value={couponForm.discount_type} onChange={(e) => setCouponForm(prev => ({ ...prev, discount_type: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} data-testid="coupon-type-select">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Value</label>
                  <input type="number" value={couponForm.discount_value} onChange={(e) => setCouponForm(prev => ({ ...prev, discount_value: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="20" data-testid="coupon-value-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Max Uses (empty = unlimited)</label>
                <input type="number" value={couponForm.max_uses} onChange={(e) => setCouponForm(prev => ({ ...prev, max_uses: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="100" data-testid="coupon-max-uses" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={couponForm.is_active} onChange={(e) => setCouponForm(prev => ({ ...prev, is_active: e.target.checked }))} />
                Active
              </label>
            </div>
            <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border-main)' }}>
              <button onClick={() => setShowCouponModal(false)} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }}>Cancel</button>
              <button onClick={saveCoupon} disabled={savingCoupon} className="btn-primary px-4 py-2 text-sm" data-testid="save-coupon-btn">{savingCoupon ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM PACKAGE MODAL */}
      {showCustomPkgModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="border w-full max-w-md rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
            <div className="p-4 sm:p-6 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
              <h3 className="text-lg font-bold">New Custom Package</h3>
              <button onClick={() => setShowCustomPkgModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>User Email</label>
                <input value={customPkgForm.user_email} onChange={(e) => setCustomPkgForm(prev => ({ ...prev, user_email: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="user@example.com" data-testid="custom-pkg-email" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Package Name</label>
                <input value={customPkgForm.name} onChange={(e) => setCustomPkgForm(prev => ({ ...prev, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="Custom VIP" data-testid="custom-pkg-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Credits</label>
                  <input type="number" value={customPkgForm.credits} onChange={(e) => setCustomPkgForm(prev => ({ ...prev, credits: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="2000" data-testid="custom-pkg-credits" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Price (USD)</label>
                  <input type="number" value={customPkgForm.price_usd} onChange={(e) => setCustomPkgForm(prev => ({ ...prev, price_usd: e.target.value }))} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }} placeholder="35" data-testid="custom-pkg-price" />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border-main)' }}>
              <button onClick={() => setShowCustomPkgModal(false)} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }}>Cancel</button>
              <button onClick={saveCustomPackage} disabled={savingCustomPkg} className="btn-primary px-4 py-2 text-sm" data-testid="save-custom-pkg-btn">{savingCustomPkg ? 'Saving...' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}





    </div>
  );
}
